import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { CardSkeleton } from '../components/Skeleton';
import { pipelineApi } from '../lib/api';

export const Route = createFileRoute('/pipeline')({
  component: PipelinePage,
});

/** 销售 Pipeline 总览：显示漏斗、近期动作和可执行的客户入口。 */
function PipelinePage() {
  const {
    data: stats,
    isLoading: statsLoading,
    error,
  } = useQuery({
    queryKey: ['pipeline', 'stats'],
    queryFn: pipelineApi.stats,
    refetchInterval: 60_000,
  });
  const { data: funnel, isLoading: funnelLoading } = useQuery({
    queryKey: ['pipeline', 'funnel'],
    queryFn: pipelineApi.funnel,
    refetchInterval: 60_000,
  });

  if (statsLoading || funnelLoading) return <CardSkeleton />;
  if (error || !stats || !funnel) {
    return (
      <div className="rounded border border-bad/30 bg-bad/10 p-4 text-sm text-bad">
        看板数据加载失败，请稍后重试。
      </div>
    );
  }

  const metrics = [
    ['本周新询盘', stats.activity.newInquiriesWeek],
    ['本周新报价', stats.activity.newQuotesWeek],
    ['本周外发', stats.outreach.sentThisWeek],
    ['邮件回复率', `${stats.outreach.replyRate}%`],
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">销售 Pipeline</h1>
          <p className="mt-1 text-sm text-ink-3">从拓客到成交，优先处理卡在下一步的客户。</p>
        </div>
        <Link to="/customers" className="text-sm text-accent hover:underline">
          查看全部客户 →
        </Link>
      </div>

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {metrics.map(([label, value]) => (
          <div key={label} className="rounded border border-line bg-surface p-4">
            <div className="text-xs text-ink-3">{label}</div>
            <div className="mt-1 text-2xl font-semibold">{value}</div>
          </div>
        ))}
      </section>

      <section className="rounded border border-line bg-surface p-4 sm:p-5">
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="font-semibold">客户漏斗</h2>
          <span className="text-xs text-ink-3">活跃客户 {stats.customer.total} 家</span>
        </div>
        <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
          {funnel.funnel.map((item, index) => (
            <div key={item.stage} className="relative rounded border border-line bg-surface-2 p-3">
              <div className="text-xs text-ink-3">{item.label}</div>
              <div className="mt-1 text-2xl font-semibold">{item.count}</div>
              <Link
                to="/customers"
                search={{ stage: item.stage }}
                className="mt-2 inline-block text-xs text-accent hover:underline"
              >
                查看客户
              </Link>
              {index < funnel.funnel.length - 1 && (
                <div className="mt-2 text-[11px] text-ink-3">→ 下一阶段 {item.toNextStage}%</div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded border border-line bg-surface p-5">
          <h2 className="font-semibold">本周外发质量</h2>
          <dl className="mt-3 space-y-2 text-sm">
            <Metric label="已发送" value={stats.outreach.sentThisWeek} />
            <Metric label="已回复" value={stats.outreach.repliedThisWeek} />
            <Metric label="退信" value={stats.outreach.bouncedThisWeek} />
          </dl>
        </div>
        <div className="rounded border border-line bg-surface p-5">
          <h2 className="font-semibold">数据准备度</h2>
          <dl className="mt-3 space-y-2 text-sm">
            <Metric
              label="已完成画像"
              value={`${stats.customer.enriched}/${stats.customer.total}`}
            />
            <Metric
              label="平均询盘至下单"
              value={
                stats.cycle.avgDaysToOrder === null
                  ? '暂无成交数据'
                  : `${stats.cycle.avgDaysToOrder} 天`
              }
            />
            <Metric label="背调通过" value={stats.backgroundCheck.passed ?? 0} />
          </dl>
        </div>
      </section>
    </div>
  );
}

/** 显示一个紧凑的指标行。 */
function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex justify-between border-b border-line pb-2 last:border-0 last:pb-0">
      <dt className="text-ink-3">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
